import { Fragment } from "react";
import { useSelector } from "react-redux";
import { Navigate, Route, Routes } from "react-router-dom";
import { selectAuth } from "../redux/features/auth/auth-slice";
import PublicRoute from "../shared/components/public-route/public-route";
import SolarSimDashboardLayout from "../shared/components/solar-sim-dashboard-layout/solar-sim-dashboard-layout";
import { UserRole } from "../shared/oversight-core/enums/user-roles";
import { AppRoute } from "../shared/oversight-core/interfaces/app-routes";
import IPageInfo from "../shared/oversight-core/interfaces/page-info";
import PrivateRoute from "../shared/oversight-core/shared-components/private-route/private-route";
import Unauthorized from "../shared/oversight-core/shared-components/unauthorized/unauthorized";
import ChangeEmail from "../shared/oversight-core/shared-pages/change-email/change-email";
import ChangeMobileNumber from "../shared/oversight-core/shared-pages/change-mobile-number/change-mobile-number";
import EmailOtpVerification from "../shared/oversight-core/shared-pages/email-otp-verification/email-otp-verification";
import EmailVerification from "../shared/oversight-core/shared-pages/email-verification/email-verification";
import ForgotPassword from "../shared/oversight-core/shared-pages/forgot-password/forgot-password";
import ResetPassword from "../shared/oversight-core/shared-pages/reset-password/reset-password";
import UserProfile from "../shared/oversight-core/shared-pages/user-profile/user-profile";
import RemoveTrailingSlash from "../shared/oversight-core/utils/removeTrailingSlash";
import BuildSystem from "./build-system/build-system";
import BuildSystemAdvanced from "./build-system/build-system-advanced/build-system-advanced";
import BuildSystemDirect from "./build-system/build-system-direct/build-system-direct";
import BuildSystemSimplified from "./build-system/build-system-simplified/build-system-simplified";
import BuildWaterHeaterSystem from "./build-system/build-water-heater-system/build-water-heater-system";
import CalculateEnergy from "./calculate-energy/calculate-energy";
import Login from "./login/login";
import PublicSettings from "./public-settings/public-settings";
import Register from "./register/register";
import EditEstimation from "./solar-sim-dashboard/estimations/edit-estimation/edit-estimation";
import Estimations from "./solar-sim-dashboard/estimations/estimations";
import ViewEstimation from "./solar-sim-dashboard/estimations/view-estimation/view-estimation";
import Settings from "./solar-sim-dashboard/settings/settings";

export const publicRoutes: IPageInfo[] = [
  {
    name: "Build System",
    path: AppRoute.PUBLIC_DESIGN_SYSTEM,
    component: <BuildSystem />,
    enableRoute: true,
    root: true,
    subRoutes: [
      {
        name: "Calculate Energy",
        path: AppRoute.PUBLIC_DESIGN_SYSTEM,
        component: <CalculateEnergy />,
        enableRoute: true,
      },
      {
        name: "Calculate Energy",
        path: AppRoute.PUBLIC_DESIGN_SYSTEM_DIRECT_ASSEMBLE_SYSTEM,
        component: <BuildSystemDirect />,
        enableRoute: true,
      },
      {
        name: "Calculate Energy",
        path: AppRoute.PUBLIC_DESIGN_SYSTEM_DIRECT_SYSTEM_SUMMARY,
        component: <BuildSystemDirect />,
        enableRoute: true,
      },
      {
        name: "Build System",
        path: AppRoute.PUBLIC_DESIGN_SYSTEM_SIMPLIFIED_USAGE_INFO,
        component: <BuildSystemSimplified />,
        enableRoute: true,
      },
      {
        name: "Build System",
        path: AppRoute.PUBLIC_DESIGN_SYSTEM_SIMPLIFIED_ASSEMBLE_SYSTEM,
        component: <BuildSystemSimplified />,
        enableRoute: true,
      },
      {
        name: "Build System",
        path: AppRoute.PUBLIC_DESIGN_SYSTEM_SIMPLIFIED_SYSTEM_SUMMARY,
        component: <BuildSystemSimplified />,
        enableRoute: true,
      },
      {
        name: "Build System",
        path: AppRoute.PUBLIC_DESIGN_SYSTEM_WATER_HEATER_ASSEMBLE_SYSTEM,
        component: <BuildWaterHeaterSystem />,
        enableRoute: true,
      },
      {
        name: "Build System",
        path: AppRoute.PUBLIC_DESIGN_SYSTEM_WATER_HEATER_SYSTEM_SUMMARY,
        component: <BuildWaterHeaterSystem />,
        enableRoute: true,
      },
      {
        name: "Settings",
        path: AppRoute.PUBLIC_DESIGN_SYSTEM_SETTINGS,
        icon: "settings",
        component: <PublicSettings />,
        enableRoute: true,
        bgColor: "color-menu-item-bg-2",
        bgColorIcon: "--color-menu-item-icon-2",
        showOnDrawer: true,
      },
    ],
  },
  {
    name: "Login",
    path: AppRoute.LOGIN,
    component: <Login />,
    enableRoute: true,
  },
  {
    name: "Register",
    path: AppRoute.REGISTER,
    component: <Register />,
  },
  {
    name: "Forgot Password",
    path: AppRoute.FORGOT_PASSWORD,
    component: <ForgotPassword />,
    enableRoute: true,
  },
  {
    name: "Reset Password",
    path: AppRoute.RESET_PASSWORD,
    component: <ResetPassword />,
    enableRoute: true,
  },
  {
    name: "Email Verification",
    path: AppRoute.EMAIL_VERIFICATION,
    component: <EmailOtpVerification />,
    enableRoute: true,
  },
];

export const getConsumerPrivateRoutes = (userRole: UserRole): IPageInfo[] => {
  const subRoutes: IPageInfo[] = [
    // {
    //   name: "Dashboard",
    //   icon: "home",
    //   path: AppRoute.HOME,
    //   component: <Home />,
    //   enableRoute: true,
    //   bgColor: "activeBg-1",
    //   bgColorIcon: "#1A3C84",
    //   showOnDrawer: false,
    //   privilege: EPrivileges.EXTERNAL_GENERATION_READ_PRIVILEGE,
    // },
    // {
    //   name: "Sites",
    //   icon: "sunny",
    //   path: AppRoute.SITES,
    //   index: true,
    //   component: <Sites />,
    //   enableRoute: true,
    //   bgColor: "activeBg-13",
    //   bgColorIcon: "#AC782B",
    //   showOnDrawer: false,
    //   privilege: EPrivileges.EXTERNAL_GENERATION_READ_PRIVILEGE,
    // },
    {
      name: "Estimations",
      icon: "solar_power",
      path: AppRoute.ESTIMATIONS,
      component: <Estimations />,
      enableRoute: true,
      bgColor: "color-menu-item-bg-1",
      bgColorIcon: "--color-menu-item-icon-1",
      showOnDrawer: true,
    },
    {
      name: "Estimations",
      icon: "solar_power",
      path: AppRoute.ESTIMATIONS + "/:id",
      component: <ViewEstimation />,
      enableRoute: true,
      bgColor: "color-menu-item-bg-1",
      bgColorIcon: "--color-menu-item-icon-1",
      showOnDrawer: false,
    },
    {
      name: "Estimations",
      icon: "solar_power",
      path: AppRoute.EDIT_ESTIMATIONS + "/:id",
      component: <EditEstimation />,
      enableRoute: true,
      bgColor: "color-menu-item-bg-1",
      bgColorIcon: "--color-menu-item-icon-1",
      showOnDrawer: false,
    },
    {
      name: "Settings",
      path: AppRoute.SETTINGS,
      icon: "settings",
      component: <Settings />,
      enableRoute: true,
      bgColor: "color-menu-item-bg-2",
      bgColorIcon: "--color-menu-item-icon-2",
      showOnDrawer: true,
    },
    {
      name: "Profile",
      path: AppRoute.USER_PROFILE,
      component: <UserProfile />,
      enableRoute: true,
      showOnDrawer: false,
    },
    {
      name: "Design System",
      path: AppRoute.DESIGN_SYSTEM,
      component: <CalculateEnergy />,
      enableRoute: true,
      showOnDrawer: false,
    },
    {
      name: "Advanced Calculation",
      path: AppRoute.DESIGN_SYSTEM_ADVANCED_DEVICES,
      component: <BuildSystemAdvanced />,
      enableRoute: true,
      showOnDrawer: false,
    },
    {
      name: "Advanced Calculation",
      path: AppRoute.DESIGN_SYSTEM_ADVANCED_USAGE_INFO,
      component: <BuildSystemAdvanced />,
      enableRoute: true,
      showOnDrawer: false,
    },
    {
      name: "Advanced Calculation",
      path: AppRoute.DESIGN_SYSTEM_ADVANCED_ASSEMBLE_SYSTEM,
      component: <BuildSystemAdvanced />,
      enableRoute: true,
      showOnDrawer: false,
    },
    {
      name: "Advanced Calculation",
      path: AppRoute.DESIGN_SYSTEM_ADVANCED_SYSTEM_SUMMARY,
      component: <BuildSystemAdvanced />,
      enableRoute: true,
      showOnDrawer: false,
    },
    {
      name: "Simplified Calculation",
      path: AppRoute.DESIGN_SYSTEM_SIMPLIFIED_USAGE_INFO,
      component: <BuildSystemSimplified />,
      enableRoute: true,
      showOnDrawer: false,
    },
    {
      name: "Simplified Calculation",
      path: AppRoute.DESIGN_SYSTEM_SIMPLIFIED_ASSEMBLE_SYSTEM,
      component: <BuildSystemSimplified />,
      enableRoute: true,
      showOnDrawer: false,
    },
    {
      name: "Simplified Calculation",
      path: AppRoute.DESIGN_SYSTEM_SIMPLIFIED_SYSTEM_SUMMARY,
      component: <BuildSystemSimplified />,
      enableRoute: true,
      showOnDrawer: false,
    },
    {
      name: "Direct Calculation",
      path: AppRoute.DESIGN_SYSTEM_DIRECT_ASSEMBLE_SYSTEM,
      component: <BuildSystemDirect />,
      enableRoute: true,
      showOnDrawer: false,
    },
    {
      name: "Direct Calculation",
      path: AppRoute.DESIGN_SYSTEM_DIRECT_SYSTEM_SUMMARY,
      component: <BuildSystemDirect />,
      enableRoute: true,
      showOnDrawer: false,
    },
    {
      name: "Water Heater Calculation",
      path: AppRoute.DESIGN_SYSTEM_WATER_HEATER_ASSEMBLE_SYSTEM,
      component: <BuildWaterHeaterSystem />,
      enableRoute: true,
      showOnDrawer: false,
    },
    {
      name: "Water Heater Calculation",
      path: AppRoute.DESIGN_SYSTEM_WATER_HEATER_SYSTEM_SUMMARY,
      component: <BuildWaterHeaterSystem />,
      enableRoute: true,
      showOnDrawer: false,
    },
  ];

  const filteredSubRoutes =
    userRole === UserRole.ROLE_USER
      ? subRoutes.filter(
          (route) => route.name !== "Dashboard" && route.name !== "Sites"
        )
      : subRoutes;

  return [
    {
      name: "Email Verification",
      path: AppRoute.USER_EMAIL_VERIFICATION,
      component: <EmailOtpVerification />,
      enableRoute: true,
    },
    {
      name: "Email OTP Verification",
      path: AppRoute.VERIFY_EMAIL_OTP,
      component: <EmailOtpVerification />,
      showOnDrawer: false,
    },
    {
      name: "Email Verification",
      path: AppRoute.VERIFY_EMAIL,
      component: <EmailVerification />,
      showOnDrawer: false,
    },
    {
      name: "Change Email",
      path: AppRoute.CHANGE_MOBILE_NUMBER,
      component: <ChangeMobileNumber />,
      showOnDrawer: false,
    },
    {
      name: "Change Email",
      path: AppRoute.CHANGE_EMAIL,
      component: <ChangeEmail />,
      showOnDrawer: false,
    },
    {
      name: "Change Password",
      path: AppRoute.CHANGE_PASSWORD,
      component: <ResetPassword />,
      enableRoute: true,
    },
    {
      name: "Solar Sim Dashboard",
      path: AppRoute.DASHBOARD,
      component: <SolarSimDashboardLayout />,
      root: true,
      subRoutes: filteredSubRoutes,
    },
  ];
};

const AppRoutes = () => {
  const auth = useSelector(selectAuth);
  const getRoutes = (routes: IPageInfo[]) => {
    return routes.map((privateRoutes: IPageInfo, index) => (
      <Route
        key={index}
        path={privateRoutes.path}
        element={privateRoutes.component}
      >
        {privateRoutes.subRoutes?.map((subRoute: IPageInfo, index) => {
          const route = [
            <Route
              key={index}
              path={subRoute.path}
              element={
                !subRoute.privilege ||
                auth.privileges?.includes(subRoute.privilege) ? (
                  subRoute.component
                ) : (
                  <Unauthorized />
                )
              }
            />,
          ];

          if (subRoute.index) {
            route.push(
              <Route
                key={index + subRoute.path}
                index
                element={<Navigate to={subRoute.path} />}
              />
            );
          }
          return route;
        })}
      </Route>
    ));
  };

  return (
    <Fragment>
      <RemoveTrailingSlash />
      <Routes>
        <Route path="/" element={<PublicRoute />}>
          <Route
            index
            element={<Navigate to={AppRoute.PUBLIC_DESIGN_SYSTEM} />}
          />
          {publicRoutes.map((p: IPageInfo, index) => {
            return (
              <Route key={index} path={p.path} element={p.component}>
                {p.subRoutes?.map((subRoute: IPageInfo, subIndex) => (
                  <Route
                    key={`${index}-${subIndex}`}
                    path={subRoute.path}
                    element={subRoute.component}
                  />
                ))}
              </Route>
            );
          })}
          <Route
            path="*"
            element={<Navigate to={AppRoute.PUBLIC_DESIGN_SYSTEM} />}
          />
        </Route>

        <Route path="/" element={<PrivateRoute role={auth.userRole} />}>
          {getRoutes(getConsumerPrivateRoutes(auth.userRole))}
        </Route>

        <Route path="/unauthorized" element={<Unauthorized />} />
      </Routes>
    </Fragment>
  );
};

export default AppRoutes;
